import { Divider, Stack, ToggleButton, ToggleButtonGroup } from 'component-library';
import { Dispatch, ReactNode, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ECOSYSTEM_CLASSIFICATION,
  ECOSYSTEM_CLASSIFICATION_FACTS_BY_LEVEL,
  EcosystemClassificationType,
} from '@/api/rest/resources/types/ecosystemClassification';
import { LABEL_FILTERS, PlotReportFactLabelNameEnum } from '@/api/rest/resources/types/fact';
import { NCCard, NCCard2SectionLayout, NCCardComplianceFooter, NCCardHeader } from '@/components/NCCard/NCCard';
import { EcosystemClassificationExplainer } from '@/components/NCExplainers/EcosystemClassificationExplainer';
import { COMPLIANCE_LOGOS_ECOSYSTEM_CLASSIFICATION } from '@/config/constants';
import { usePlotFacts } from '@/pages/shared/hooks/usePlotFact';
import { getFactsFromFactArray } from '@/pages/shared/hooks/utils';
import { isNullish } from '@/utils/booleans';

import { PlotCardWrapper } from '../PlotCardWrapper';
import { EcosystemClassificationGraphTile } from './GraphTile';
import { EcosystemClassificationStatsTile } from './StatsTile';

export const EcosystemClassification = () => {
  const { t } = useTranslation();

  return (
    <PlotCardWrapper name={t('shared.ncaDetail.ecosystemClassification.labels.ecosystemClassification')}>
      <EcosystemClassificationCard />
    </PlotCardWrapper>
  );
};

const EcosystemClassificationCard = () => {
  const [selectedClassification, setSelectedClassification] = useState<EcosystemClassificationType>(
    ECOSYSTEM_CLASSIFICATION.IUCN_GET,
  );

  return (
    <EcosystemClassificationCardBase classification={selectedClassification}>
      <EcosystemClassificationSelector
        selectedClassification={selectedClassification}
        setSelectedClassification={setSelectedClassification}
      />
    </EcosystemClassificationCardBase>
  );
};

export const EcosystemClassificationSelector = ({
  selectedClassification,
  setSelectedClassification,
}: {
  selectedClassification: EcosystemClassificationType;
  setSelectedClassification: Dispatch<SetStateAction<EcosystemClassificationType>>;
}) => {
  const { t } = useTranslation();

  return (
    <Stack className='w-full py-4' center>
      <ToggleButtonGroup value={selectedClassification} onChange={setSelectedClassification} size='small'>
        {Object.values(ECOSYSTEM_CLASSIFICATION).map((classification) => (
          <ToggleButton key={classification} value={classification}>
            {t(`shared.ncaDetail.ecosystemClassification.labels.categories.${classification}`)}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Stack>
  );
};

const EcosystemClassificationCardBase = ({
  children,
  classification,
}: {
  children: ReactNode;
  classification: EcosystemClassificationType;
}) => {
  const { t } = useTranslation();

  const factNames = ECOSYSTEM_CLASSIFICATION_FACTS_BY_LEVEL[`${classification}`];

  const facts = usePlotFacts(Object.values(factNames).filter((f) => !isNullish(f)) as string[], undefined, {
    [PlotReportFactLabelNameEnum.class_name]: LABEL_FILTERS.ALL,
  });

  const firstScalarFacts =
    getFactsFromFactArray<number>(facts, factNames.firstScalarFact)?.filter((fact) => !isNullish(fact.value)) ?? [];
  const secondScalarFacts = factNames.secondScalarFact
    ? (getFactsFromFactArray<number>(facts, factNames.secondScalarFact)?.filter((fact) => !isNullish(fact.value)) ?? [])
    : [];
  const graphFacts =
    getFactsFromFactArray<number>(facts, factNames.graphFact)?.filter((fact) => !isNullish(fact.value)) ?? [];

  const hasData = firstScalarFacts.length > 0 || secondScalarFacts.length > 0 || graphFacts.length > 0;

  if (!hasData) {
    return null;
  }

  return (
    <NCCard
      explainer={<EcosystemClassificationExplainer category={classification} />}
      data-testid='ecosystem-classification-nca'
    >
      <NCCardHeader
        title={t('shared.ncaDetail.ecosystemClassification.labels.ecosystemClassification')}
        className='lg:grid-cols-[1fr_1fr]'
      />
      {children}
      <Divider />
      <NCCard2SectionLayout>
        <EcosystemClassificationGraphTile graphFacts={graphFacts} category={classification} />
        <EcosystemClassificationStatsTile
          firstScalarFacts={firstScalarFacts}
          secondScalarFacts={secondScalarFacts}
          category={classification}
        />
      </NCCard2SectionLayout>

      <NCCardComplianceFooter logos={COMPLIANCE_LOGOS_ECOSYSTEM_CLASSIFICATION} />
    </NCCard>
  );
};
